import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore, libraryStore, wishlistStore } from '../../store';
import { Loader, showToast, dynamicIsland } from '../../components/shared';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';

// Login screen — v2 design (2026-05-30).
//
// Layout:
//   - Lavender wash background with subtle floating-icon decorations.
//   - White rounded card centered: logo, "KleverBook" wordmark, tagline.
//   - Email + Password fields below.
//   - Remember-me checkbox left + "Forgot Password?" link right.
//   - Indigo "Login" button.
//   - "Don't have an account? Sign Up" link at the bottom.
//
// Per Mike: no SSO buttons. The mock shows Google + Apple — they're
// hidden until the OAuth backend lands (separate PR with new env vars,
// Firebase, Apple Developer cert, etc).

const BRAND = '#5B47E0';
const BRAND_DARK = '#4A37CC';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';

const LoginScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleLogin = () => {
    if (!email.trim()) {
      showToast('Please enter your email', 'error');
      return;
    }
    if (!emailRegex.test(email)) {
      showToast('Please enter a valid email', 'error');
      return;
    }
    if (!password.trim()) {
      showToast('Please enter your password', 'error');
      return;
    }

    userStore.attemptToLogin({ email: email.trim(), password }, (success, message) => {
      if (success) {
        libraryStore.getOwnedLibrary().catch(() => {});
        wishlistStore.load().catch(() => {});
        const name = userStore.user?.name || email.trim();
        const greeting = userStore.isAuthor
          ? {
              title: `Welcome back, ${name}`,
              subtitle: userStore.requiresOnboarding
                ? 'Finish setup in Profile → Author Tools'
                : 'Reader + author tools available in Profile',
            }
          : { title: 'Welcome back', subtitle: name };
        dynamicIsland.show({ kind: 'success', ...greeting });
        navigate('/app/home', { replace: true });
      } else {
        dynamicIsland.show({
          kind: 'error',
          title: 'Login failed',
          subtitle: message || 'Check your email and password',
        });
      }
    });
  };

  return (
    <div
      className="h-full relative overflow-y-auto"
      style={{
        background:
          'linear-gradient(180deg, #F8F5FE 0%, #EEEAFB 50%, #E5DFF9 100%)',
      }}
    >
      <Loader visible={userStore.loginLoader} message="Logging in..." />

      {/* Decorative floating icons (purely visual) */}
      <FloatingDecorations />

      <div className="relative min-h-full flex flex-col items-center justify-center px-6 py-10 safe-area-top safe-area-bottom">
        {/* === Logo card === */}
        <div className="w-full max-w-sm flex flex-col items-center mb-8">
          <div
            className="w-[100px] h-[100px] rounded-3xl bg-white shadow-[0_20px_50px_-10px_rgba(91,71,224,0.25)] flex items-center justify-center mb-5 p-3"
            style={{ border: '1px solid rgba(91,71,224,0.08)' }}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/kleverbook_logo_new.png`}
              alt="KleverBook"
              className="w-full h-full object-contain rounded-2xl"
            />
          </div>

          <h1
            className="text-[36px] font-bold tracking-tight"
            style={{ color: TEXT_PRIMARY, lineHeight: 1.1 }}
          >
            KleverBook
          </h1>
          <p
            className="text-[15px] font-medium mt-1"
            style={{ color: BRAND, letterSpacing: '0.01em' }}
          >
            Read. Discover. Inspire.
          </p>
        </div>

        {/* === Form === */}
        <div className="w-full max-w-sm space-y-4">
          <Field
            icon={<Mail className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="Email address"
            autoComplete="email"
          />

          <Field
            icon={<Lock className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            placeholder="Password"
            autoComplete="current-password"
            trailing={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword
                  ? <EyeOff className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />
                  : <Eye className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} />}
              </button>
            }
          />

          {/* Remember me + forgot password row */}
          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded accent-[#5B47E0] cursor-pointer"
              />
              <span className="text-[14px]" style={{ color: TEXT_MUTED }}>
                Remember me
              </span>
            </label>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-[14px] font-medium"
              style={{ color: BRAND }}
            >
              Forgot Password?
            </button>
          </div>

          {/* === Login button === */}
          <button
            onClick={handleLogin}
            className="w-full h-12 rounded-2xl text-white text-[16px] font-semibold transition-transform active:scale-[0.98] mt-2"
            style={{
              background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
              boxShadow: '0 12px 28px -8px rgba(91, 71, 224, 0.5)',
            }}
          >
            Login
          </button>
        </div>

        {/* === Sign up footer === */}
        <div className="mt-8 text-center">
          <span className="text-[14px]" style={{ color: TEXT_MUTED }}>
            Don&apos;t have an account?{' '}
          </span>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="text-[14px] font-semibold"
            style={{ color: BRAND }}
          >
            Sign Up
          </button>
        </div>
      </div>
    </div>
  );
});

/* ====================================================================
 *  Field — text/password input matching the v2 spec
 * ==================================================================== */

function Field({
  icon,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  trailing,
}: {
  icon: React.ReactNode;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoComplete?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      className="relative h-12 rounded-2xl flex items-center px-4"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E2DFF5',
      }}
    >
      <span className="mr-3 flex-shrink-0">{icon}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="flex-1 bg-transparent outline-none text-[15px]"
        style={{ color: TEXT_PRIMARY }}
      />
      {trailing && <span className="ml-2 flex-shrink-0">{trailing}</span>}
    </div>
  );
}

/* ====================================================================
 *  FloatingDecorations — purely visual; soft purple circles + sparkles
 * ==================================================================== */

function FloatingDecorations() {
  // Position is fixed so they don't shift while the form scrolls.
  return (
    <>
      <div
        aria-hidden="true"
        className="absolute top-12 -left-10 w-40 h-40 rounded-full opacity-40"
        style={{
          background: 'radial-gradient(circle, rgba(91,71,224,0.18), transparent 70%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute top-32 -right-12 w-44 h-44 rounded-full opacity-40"
        style={{
          background: 'radial-gradient(circle, rgba(247,123,176,0.16), transparent 70%)',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute bottom-20 left-4 w-32 h-32 rounded-full opacity-30"
        style={{
          background: 'radial-gradient(circle, rgba(91,71,224,0.14), transparent 70%)',
        }}
      />
    </>
  );
}

export default LoginScreen;
